/**
 * The window before a redemption moves money, expressed as rules.
 *
 * A redemption sits in `requested` until a manager approved it and the drain
 * sees `eligible_at` pass and posts the ledger debit. Everything here is about
 * that window: whether it is still open, how long is left, whether it still
 * waits for its approval, and whether stopping is still possible.
 */
import type { RedemptionRequest } from '../../services/redemptions.service';

/** Only a request that has not drained can be stopped. After the debit is
 *  posted, undoing it is the reversal path, not a cancel. */
export function canCancel(
  r: Pick<RedemptionRequest, 'status' | 'ledger_entry_id' | 'cancellable'>,
): boolean {
  // A notice of non-renewal can no longer be withdrawn from its exit day (the server says so).
  return r.status === 'requested' && r.ledger_entry_id === null && r.cancellable !== false;
}

/** The reason the server asks for to stop an exit given for the estate, in characters. */
export const ESTATE_STOP_REASON_MIN = 10;

/** Who may stop it: anyone on staff, except an exit given for the estate of a deceased holder,
 *  which only a manager withdraws, with a reason (the server also refuses the client's own
 *  Account Executive). */
export function canStop(
  r: Pick<RedemptionRequest, 'status' | 'ledger_entry_id' | 'cancellable' | 'given_for_estate'>,
  role: string | null,
): boolean {
  if (!canCancel(r)) return false;
  return r.given_for_estate !== true || role === 'manager' || role === 'super_admin';
}

export function whyCannotCancel(
  r: Pick<
    RedemptionRequest,
    'status' | 'ledger_entry_id' | 'cancellable' | 'exit_on' | 'withdrawable_until'
  >,
): string | null {
  if (canCancel(r)) return null;
  if (r.status === 'requested' && r.ledger_entry_id === null) {
    // The fund answers a notice with its last day to withdraw it; until then it is not known.
    if (r.exit_on && !r.withdrawable_until) {
      return 'This notice not to renew is still being registered with the fund; it can be withdrawn once it is.';
    }
    return 'The last day to withdraw this notice not to renew has passed: the decision is fixed.';
  }
  if (r.ledger_entry_id !== null) {
    return 'The money has already left this client’s balance. Reversing it is a different action.';
  }
  switch (r.status) {
    case 'cancelled':
      return 'Already cancelled.';
    case 'submitted':
      return 'Already sent to the fund.';
    default:
      return `No longer pending (${r.status}).`;
  }
}

/** What the approver reads about a notice of non-renewal (S-61): its exit day, as funds gave it,
 *  and, once that day has passed (the server says so), that approving sends it at once, dated to
 *  that day by funds. Null for any other redemption. */
export function noticeLine(
  r: Pick<RedemptionRequest, 'status' | 'exit_on' | 'cancellable'>,
  calendarDate: (day: string) => string,
): string | null {
  if (!r.exit_on) return null;
  const day = calendarDate(r.exit_on);
  if (r.status === 'requested' && r.cancellable === false) {
    return `Notice not to renew. Exit day ${day} has passed: approving sends it at once, dated to that day.`;
  }
  return `Notice not to renew. Exit day ${day}.`;
}

/** The date alone never releases a redemption: a manager has to approve it. */
export function awaitsApproval(r: Pick<RedemptionRequest, 'status' | 'approved_at'>): boolean {
  return r.status === 'requested' && r.approved_at === null;
}

/** Approving is a manager's act; an agent sees what is waiting and can stop it. */
export function canApprove(
  r: Pick<RedemptionRequest, 'status' | 'approved_at'>,
  role: string | null,
): boolean {
  return awaitsApproval(r) && (role === 'manager' || role === 'super_admin');
}

export type EligibilityState = 'due' | 'soon' | 'waiting';

export interface Eligibility {
  state: EligibilityState;
  /** Whole hours until the drain may post the debit; 0 once it is due. */
  hoursLeft: number;
  text: string;
}

/**
 * How much of the window is left, from a reference instant the caller supplies.
 *
 * `now` is a parameter rather than `Date.now()` so this is deterministic and
 * testable — the same rule the backend's business-date helpers follow.
 * 'soon' is under 24 hours: the point of this screen is to act before the drain,
 * and a row that says "in 3 hours" reads differently from one that says "in 9 days".
 * A date that has come moves money only when the redemption is `approved`.
 */
export function describeEligibility(eligibleAt: string, now: Date, approved = true): Eligibility {
  const due = new Date(eligibleAt).getTime();
  const ms = due - now.getTime();
  if (Number.isNaN(due)) return { state: 'waiting', hoursLeft: 0, text: 'Unknown' };
  if (ms <= 0) {
    return {
      state: 'due',
      hoursLeft: 0,
      text: approved
        ? 'Due now — the next sweep will post it'
        : 'Its date has come — nothing moves until a manager approves it',
    };
  }
  const hours = Math.ceil(ms / 3_600_000);
  if (hours <= 24) {
    return { state: 'soon', hoursLeft: hours, text: `In ${hours} hour${hours === 1 ? '' : 's'}` };
  }
  const days = Math.ceil(hours / 24);
  return { state: 'waiting', hoursLeft: hours, text: `In ${days} day${days === 1 ? '' : 's'}` };
}

/** Most urgent first: what is about to move money is what a person must see. */
export function sortByUrgency(rows: RedemptionRequest[], now: Date): RedemptionRequest[] {
  return [...rows].sort((a, b) => {
    const ea = describeEligibility(a.eligible_at, now).hoursLeft;
    const eb = describeEligibility(b.eligible_at, now).hoursLeft;
    if (ea !== eb) return ea - eb;
    return a.created_at.localeCompare(b.created_at);
  });
}

/** The client's name, or their email when the name is not set. */
export function clientLabel(r: RedemptionRequest): string {
  const name = `${r.user_first_name ?? ''} ${r.user_last_name ?? ''}`.trim();
  return name || r.user_email;
}
